import { addMonths, format } from 'date-fns'

import type {
  TSimulacaoGrupoParcelaMensais,
  TSimulacaoParcelaSaldo,
} from '@/domain/simulador/schemas/simulacao-grupo-parcela.schema'
import type { IFluxoPagamento } from '@/http/get-fluxo-pagamentos-simulacao.service'
import type {
  TGrupoIntermediarias,
  TGrupoMensais,
  TGrupoParcelas,
} from '@/utils/calculadora-valores-totais/processador-valores-decimal'
import { dateToUTC } from '@/utils/date.utils'

type TParcelaComTipo = {
  data: string
  valor: number
  tipo: 'Entrada' | 'Mensais' | 'Intermediarias' | 'Saldo' | 'PosConclusao' | 'IntermediariasPosConclusao'
}

export class ProcessadorParcelasHelper {
  private static processarGrupoComQuantidade(
    grupo: Array<{ dataInicio?: string | null; valor: number; quantidade: number }> | undefined,
    tipo: 'Mensais' | 'PosConclusao',
  ): TParcelaComTipo[] {
    if (!grupo) return []
    const parcelas: TParcelaComTipo[] = []

    grupo.forEach(({ dataInicio, valor, quantidade }) => {
      if (!dataInicio) return

      for (let i = 0; i < quantidade; i++) {
        parcelas.push({
          data: format(addMonths(new Date(dateToUTC(dataInicio)), i), 'yyyy-MM-dd'),
          valor,
          tipo,
        })
      }
    })

    return parcelas
  }

  static processarEntradas(entradas?: TGrupoParcelas): TParcelaComTipo[] {
    if (!entradas) return []
    return entradas
      .filter(({ data }) => data)
      .map(({ data, valor }) => ({ data: data!, valor, tipo: 'Entrada' as const }))
  }

  static processarMensais(mensais?: TSimulacaoGrupoParcelaMensais): TParcelaComTipo[] {
    return this.processarGrupoComQuantidade(mensais, 'Mensais')
  }

  static processarIntermediarias(
    tipo: 'Intermediarias' | 'IntermediariasPosConclusao',
    intermediarias?: TGrupoIntermediarias[],
  ): TParcelaComTipo[] {
    if (!intermediarias) return []

    const parcelas: TParcelaComTipo[] = []

    intermediarias.forEach(({ parcelas: datas, valor }) => {
      datas.forEach(({ data }) => {
        if (data) {
          parcelas.push({ data, valor, tipo: tipo })
        }
      })
    })

    return parcelas
  }

  static processarPosConclusao(posConclusao?: TGrupoMensais | null): TParcelaComTipo[] {
    return this.processarGrupoComQuantidade(posConclusao ? [posConclusao] : undefined, 'PosConclusao')
  }

  static processarSaldo(saldo?: TSimulacaoParcelaSaldo | null): TParcelaComTipo[] {
    if (!saldo?.valor || !saldo?.data) return []
    return [{ data: saldo.data, valor: saldo.valor, tipo: 'Saldo' as const }]
  }

  static agruparEFormatar(
    todasParcelas: TParcelaComTipo[],
    formatFn: (valor: number) => string,
    taxaCambio?: {
      valor: number
      formatFn: (valor: number) => string
    },
  ): IFluxoPagamento[] {
    const parcelasPorAno = todasParcelas.reduce((acc: Record<number, TParcelaComTipo[]>, parcela) => {
      const ano = new Date(parcela.data).getFullYear()
      acc[ano] ??= []
      acc[ano].push(parcela)
      return acc
    }, {})

    return Object.keys(parcelasPorAno)
      .map(Number)
      .sort((a, b) => a - b)
      .map((ano) => ({
        ano,
        parcelas: (parcelasPorAno[ano] ?? [])
          .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
          .map((parcela) => ({
            data: new Intl.DateTimeFormat('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              timeZone: 'UTC',
            }).format(new Date(parcela.data)),
            tipoDaParcela: parcela.tipo,
            valor: formatFn(parcela.valor),
            valorConvertido: taxaCambio?.valor ? taxaCambio.formatFn(parcela.valor * taxaCambio.valor) : undefined,
          })),
      }))
  }
}
